import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Button,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import Select from 'react-select';
import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import UploadProgressModal from '../../../Components/Common/UploadProgressModal';
import { uploadDocumentFile, fetchParentCategories } from '../../../slices/thunks';
import { getAuthUser } from '../../../helpers/utils';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview
);

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const AddKnowledgeDocument = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = getAuthUser();

  const filePondRef = useRef(null);
  const pendingUploadRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { parentCategories, loading } = useSelector(state => state.Category);
  const { uploadProgress } = useSelector(state => state.AIDoc);

  /* -------------------- Validation -------------------- */
  const validationSchema = Yup.object({
    category: Yup.object().required(t('Category is required')),
  });

  const formik = useFormik({
    initialValues: {
      category: null,
    },
    validationSchema,
    onSubmit: () => {},
  });

  /* -------------------- Fetch Categories -------------------- */
  useEffect(() => {
    dispatch(fetchParentCategories({ DocType: 3 }));
  }, [dispatch]);

  /* -------------------- FilePond Intercept -------------------- */
  const processPdf = async (fieldName, file, _, load, error, __, abort) => {
    pendingUploadRef.current = { file, load, error, abort };
    setConfirmModalOpen(true);

    return {
      abort: () => abort(),
    };
  };

  /* -------------------- Actual Upload -------------------- */
  const startActualUpload = async () => {
    const { file, load, error } = pendingUploadRef.current;

    if (file.type !== 'application/pdf') {
      toast.error(t('Invalid PDF file'));
      error('Invalid file');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('File size exceeds 50MB'));
      error('File too large');
      return;
    }

    setConfirmModalOpen(false);
    setUploading(true);
    setLogs([]);
    setLogModalOpen(true);

    try {
      await new Promise((resolve, reject) => {
        const onProgress = ({ event, data }) => {
          if (data?.message) {
            setLogs(prev => [...prev, data.message]);
          }

          if (event === 'duplicate') {
            toast.error(t('Duplicate file'));
            reject();
          }

          if (event === 'all_done' || event === 'done') {
            toast.success(t('Document uploaded successfully'));
            resolve();
          }
        };

        const formData = new FormData();
        formData.append('file', file);
        formData.append('UserID', user.ID);
        formData.append('DocumentTypeID', 3);
        formData.append('Category', formik.values.category.label);
        formData.append('CategoryID', formik.values.category.value);
        formData.append('IsBook', false);

        dispatch(uploadDocumentFile({ formData, onProgress }));
      });

      load(file.name);
      navigate('/ai/knowledge-document');
    } catch {
      error('Upload failed');
    } finally {
      setUploading(false);
      setLogModalOpen(false);
      pendingUploadRef.current = null;
    }
  };

  /* -------------------- Options -------------------- */
  const categoryOptions = parentCategories.map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.CategoryNameAr : cat.CategoryNameEn,
  }));

  return (
    <div className="page-content">
      {/* Upload Logs Modal */}
      <UploadProgressModal
        isOpen={logModalOpen}
        logs={logs}
        progress={uploadProgress}
        onClose={() => setLogModalOpen(false)}
        isRTL={isRTL}
      />

      {/* Confirmation Modal */}
      <Modal isOpen={confirmModalOpen} centered>
        <ModalHeader toggle={() => setConfirmModalOpen(false)}>
          {t('Confirmation')}
        </ModalHeader>
        <ModalBody>
          {t('Are you sure you are not uploading a book?')}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={startActualUpload}>
            {t('Yes')}
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setConfirmModalOpen(false);
              pendingUploadRef.current = null;
              navigate('/ai/book-upload');
            }}
          >
            {t('No')}
          </Button>
        </ModalFooter>
      </Modal>

      <Container fluid>
        <Row className="mb-4 align-items-center">
          <Col>
            <h4>{t('Add Knowledge Document')}</h4>
          </Col>
          <Col xs="auto">
            <Button
              color="soft-secondary"
              disabled={uploading}
              onClick={() => navigate(-1)}
            >
              {t('Go Back')}
            </Button>
          </Col>
        </Row>

        <Form>
          <Row className="justify-content-center">
            <Col lg={6}>
              <Card>
                <CardBody>
                  {/* Category */}
                  <FormGroup className="mb-4">
                    <Label>{t('Category')}</Label>
                    <Select
                      options={categoryOptions}
                      isLoading={loading}
                      value={formik.values.category}
                      onChange={val => formik.setFieldValue('category', val)}
                      onBlur={() => formik.setFieldTouched('category', true)}
                      placeholder={t('Select category')}
                      isDisabled={uploading}
                    />
                    {formik.touched.category && formik.errors.category && (
                      <FormFeedback className="d-block">
                        {formik.errors.category}
                      </FormFeedback>
                    )}
                  </FormGroup>

                  {/* PDF Upload */}
                  <FormGroup>
                    <Label>{t('Upload PDF')}</Label>
                    <FilePond
                      ref={filePondRef}
                      allowMultiple={false}
                      acceptedFileTypes={['application/pdf']}
                      maxFileSize="50MB"
                      server={{ process: processPdf }}
                      disabled={!formik.values.category || uploading}
                      labelIdle={`${t('Drag & drop PDF or')} <span class="filepond--label-action">${t('Browse')}</span>`}
                    />
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddKnowledgeDocument;